import React, { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Box, TextField } from '@the-platform-group/component-library'
import { useModalContext } from '@the-platform-group/component-library/Modal/ModalContext'
import { useNotification } from 'components/Notification'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import SecuritySelect from 'components/Select/SecuritySelect'
import { updateCustomer } from 'services/customers'
import useImportUser from './useAddUser'
import { USERS_QUERY_KEY } from './useUsers'

// Controlled inner component so SecuritySelect can show the selected value while
// still using a ref in the parent to read the selection at save time.
function ConfigureSecurityContent({ onSelect }) {
  const [security, setSecurity] = useState(null)
  return (
    <SecuritySelect
      label="Default Security"
      value={security}
      onChange={value => {
        setSecurity(value)
        onSelect(value)
      }}
    />
  )
}

/**
 * Provides a handler to open an "Add FDS User" modal via ModalContext.
 * The modal contains separate FactSet Username and Serial Number inputs; on save
 * it calls importUser with "{username}-{serial}". If the created customer is
 * needs_configuration (no default security yet), a follow-up modal prompts CS to
 * configure it before the user can log in.
 * @returns {object} - { openAddUserModal }
 */
function useAddUserModal() {
  const queryClient = useQueryClient()
  const { handleOpenModal, handleCloseModal, updateModalState } = useModalContext()
  const { createNotification } = useNotification()
  const { createConfirmationDialog } = useConfirmationDialog()
  const { importUser } = useImportUser()
  const usernameRef = useRef('')
  const serialRef = useRef('')

  const { mutate: configureDefaultSecurity } = useMutation({
    mutationFn: ({ id, defaultSecurityId }) => updateCustomer({ id, defaultSecurityId }),
    onMutate: () => {
      updateModalState({ isLoading: true })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] })
      handleCloseModal()
    },
    onError: () => {
      createNotification({
        message: 'Failed to configure default security.',
        variant: 'error',
      })
    },
    onSettled: () => {
      updateModalState({ isLoading: false })
    },
  })

  const openConfigureSecurityModal = customerId => {
    const securityRef = { current: null }

    handleOpenModal({
      title: 'Configure Default Security',
      content: (
        <ConfigureSecurityContent
          onSelect={s => {
            securityRef.current = s
          }}
        />
      ),
      actionProps: {
        saveButtonText: 'Save',
        handleCancel: () => {
          createConfirmationDialog({
            title: 'Skip Security Configuration?',
            body: "The user's customer remains locked until a default security is configured.",
            confirmButtonText: 'Skip',
            onConfirm: handleCloseModal,
            onCancel: () => openConfigureSecurityModal(customerId),
          })
        },
        handleSave: () => {
          const security = securityRef.current
          if (!security) {
            return
          }
          configureDefaultSecurity({ id: customerId, defaultSecurityId: security.id })
        },
      },
    })
  }

  const openAddUserModal = () => {
    usernameRef.current = ''
    serialRef.current = ''

    handleOpenModal({
      title: 'Add User',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="FactSet Username"
            onChange={e => {
              usernameRef.current = e.target.value
            }}
            fullWidth
            autoFocus
          />
          <TextField
            label="Serial Number"
            onChange={e => {
              serialRef.current = e.target.value
            }}
            fullWidth
          />
        </Box>
      ),
      actionProps: {
        saveButtonText: 'Save',
        handleSave: () => {
          const username = usernameRef.current.trim()
          const serial = serialRef.current.trim()
          if (!username || !serial) {
            return
          }
          const value = `${username}-${serial}`
          updateModalState({ isLoading: true })
          importUser(value, {
            onSuccess: response => {
              handleCloseModal()
              const needsConfigAuth = response.customerAuthorizations?.find(
                ca => ca.customer?.status === 'needs_configuration',
              )
              if (needsConfigAuth) {
                openConfigureSecurityModal(needsConfigAuth.customer.id)
              }
            },
            onSettled: () => {
              updateModalState({ isLoading: false })
            },
          })
        },
      },
    })
  }

  return { openAddUserModal }
}

export default useAddUserModal
